<template>
  <div class="relative w-full">
    <label
      v-if="label"
      :for="id"
      class="mb-1 font-medium"
      :class="{ 'sr-only': hideLabel }"
    >
      {{ label }}
    </label>
    <select
      :id="id"
      v-model="value"
      class="form-select w-full rounded focus:border-black focus:ring-black"
      :class="{
        'text-neutral-30': semiDisabled,
      }"
      :aria-label="label"
      :disabled="!!disabled"
      @focus="onFocus"
    >
      <template v-for="(val, idx) in options" :key="idx">
        <option
          v-if="!required && placeholderPosition === idx"
          value="--"
          disabled
          :selected="!value"
        >
          {{ $i18n.t('select_picker.disabled_default_label') }}
        </option>
        <option :value="val.key" :disabled="!!val.disabled">
          {{ val.label }}
        </option>
      </template>
    </select>
  </div>
</template>

<script setup lang="ts">
import type { Attributes } from '@scayle/storefront-nuxt'
import type { Option } from '~/types/pdp'
const $i18n = useI18n()
const props = defineProps({
  label: {
    type: String as PropType<string>,
    default: '',
  },
  options: {
    type: Array as PropType<Option[] | Attributes[]>,
    default: undefined,
  },
  disabled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  semiDisabled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  hideLabel: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  id: {
    type: String as PropType<string>,
    required: true,
  },
  required: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false,
  },
  placeholderBeforeValue: {
    type: Number,
    default: undefined,
  },
})

const value = defineModel<string | number>()

const emit = defineEmits(['input', 'focus'])

const onFocus = () => {
  if (props.semiDisabled) {
    emit('focus' as any)
  }
}

const placeholderPosition = computed(() => {
  if (props.placeholderBeforeValue === undefined) {
    return 0
  }
  const index = (props.options || []).findIndex(
    (option) =>
      Number(props.placeholderBeforeValue) <=
      Number(String(option.key).replace(',', '.')),
  )
  return Math.max(0, index)
})
</script>

<style lang="scss">
.form-select {
  background-image: url('~/assets/icons/fielmann/ChevronDown.svg?inline');
  background-repeat: no-repeat;
  background-size: 1.8rem;
  appearance: none;
}
</style>
